import {
  Erc4626ReadAbi,
  SaturnDepositFeeAbi,
  SaturnMinWithdrawalAbi,
  SaturnPreviewDepositAbi,
  TotalSupplyAbi,
} from '../abis'
import type { SavingsRegistryEntry } from '../registry'
import { ONE_E18, toBigInt, type SavingsReader } from './shared'

/**
 * Saturn `sUSDat` — the generic 4626 reads plus the three live dials that
 * decide what entering and leaving actually cost.
 *
 *   1. `totalAssets()`
 *   2. `totalSupply()`
 *   3. `convertToAssets(1e18)`      — the redeemable price
 *   4. `previewDeposit(1 USDat)`    — the ENTRY price, net of the fee dial
 *   5. `depositFeeBps()`            — 0 at integration, admin-settable to 500
 *   6. `MIN_WITHDRAWAL()`           — 10 USDat; gates the only exit route
 *
 * ## Why not the plain `erc4626` reader
 *
 * Every view it calls is honest here, so the row would *look* right — which is
 * the problem. Two facts it cannot see change what the row means:
 *
 * **The entry price is not `1/exchangeRate`.** `_deposit` charges
 * `depositFeeBps` as a separate `transferFrom` to `feeRecipient` and mints on
 * the remainder, and only `previewDeposit` nets it. The dial reads **0** today
 * against a `MAX_DEPOSIT_FEE_BPS` of 500 while Saturn's docs claim 10 bps — so
 * the docs are already wrong in one direction, and pinning either number is
 * wrong in whichever direction governance moves next (the `erc4626-cooldown`
 * argument: Brix documented a 3-day cooldown while the contract answered 300
 * seconds). Taking the entry from `previewDeposit` means the row is correct on
 * the day it changes rather than the day someone notices.
 *
 * **The exit has a floor.** `requestRedeem` reverts below `MIN_WITHDRAWAL`, and
 * this vault has exactly one exit route, so that floor is the difference
 * between "queued" and "not leaving". It reaches the row as
 * `exitRoutes[].minAmount`, the field Treehouse exists for — there the 5 bps
 * queue's 50 wstETH floor is what pushes small holders onto the 0.5 % leg.
 *
 * ## What this reader deliberately does NOT do
 *
 * It does not touch `strcBalance()` / `getStrcOracle()`, even though
 * `totalAssets = usdatBalance + (strcBalance − unvested) × price` and the STRC
 * leg is 96.5 % of NAV. The concentration is a fact about the row's RISK, not
 * about its state, and it is stated where a reader cannot go stale on it: the
 * registry `description` and `yieldProfile: 'volatile'`. Reading it here would
 * add two calls per fetch to re-derive a number `totalAssets()` already
 * returns.
 *
 * `liquidity` stays `0` — the mode is `request-based` and there is no instant
 * leg to size. The vault holds ~$2.5 M of idle USDat against $73 M of TVL, and
 * publishing that as withdrawable would be false twice over: it is the
 * processor's working balance, and no user-callable path pays out of it.
 */
export const readerSaturnVault = (
  entry: SavingsRegistryEntry,
): SavingsReader => {
  const shareUnit = 10n ** BigInt(entry.decimals)
  const underlyingUnit =
    10n ** BigInt(entry.underlyingDecimals ?? entry.decimals)
  return {
    calls: [
      { address: entry.address, name: 'totalAssets', params: [] },
      { address: entry.address, name: 'totalSupply', params: [] },
      { address: entry.address, name: 'convertToAssets', params: [shareUnit] },
      {
        address: entry.address,
        name: 'previewDeposit',
        params: [underlyingUnit],
      },
      { address: entry.address, name: 'depositFeeBps', params: [] },
      { address: entry.address, name: 'MIN_WITHDRAWAL', params: [] },
    ],
    abis: [
      Erc4626ReadAbi,
      TotalSupplyAbi,
      Erc4626ReadAbi,
      SaturnPreviewDepositAbi,
      SaturnDepositFeeAbi,
      SaturnMinWithdrawalAbi,
    ],
    parse: ([assets, supply, rate, previewShares, feeBps, minWithdrawal]) => {
      const totalAssets = toBigInt(assets)
      const totalSupply = toBigInt(supply)
      const convertToAssetsRaw = toBigInt(rate)
      if (
        totalAssets === undefined ||
        totalSupply === undefined ||
        convertToAssetsRaw === undefined
      ) {
        return undefined
      }

      // The three dials are EXTRAS: a failure on any of them leaves that field
      // absent rather than dropping a row whose core state read fine. Absent is
      // the honest value — `depositFeeBps: undefined` means "not read", which
      // downstream is a different claim from `0` ("read, and free").
      const entryShares = toBigInt(previewShares)
      const depositFeeBps = toBigInt(feeBps)
      const exitMinAmount = toBigInt(minWithdrawal)

      return {
        totalAssets,
        totalSupply,
        exchangeRate: (convertToAssetsRaw * ONE_E18) / underlyingUnit,
        // Shares minted for ONE underlying unit, net of the fee. Equals the
        // derived `1/exchangeRate` while the dial is 0, and diverges the
        // moment it is not.
        convertToShares: entryShares,
        depositFeeBps:
          depositFeeBps !== undefined ? Number(depositFeeBps) : undefined,
        exitMinAmount,
      }
    },
  }
}
